/**
 * rmsDocumentsDashboard.js
 * ========================
 * Documents dashboard for the RMS (Referral Management System), styled to match
 * the ADHD assessment's DocumentsDashboard.js design language.
 *
 * Mirrors the ADHD dashboard layout:
 *   - RmsTopNavBar with breadcrumb (Home → Patient Overview → Documents)
 *   - Patient info panel (name, demographics, "Go to Patient Overview" link)
 *   - Assessment Report panel with:
 *       - Title + overall progress bar + Download button
 *       - Connected document sections (numbered circles with a vertical connector line)
 *       - Per-section status icons, reviewer info, and action buttons
 *
 * Key differences from the ADHD version:
 *   - Single document section ("Triage Outcome Letter") instead of 6+ sections
 *   - Uses the RMS colour palette (primary = #43213e) instead of violet/purple
 *   - Real-time Firestore listener for processing status (same pattern)
 *   - No rating scales, QbCheck, or pre-assessment panels
 *
 * Firestore path listened to:
 *   rms_organisations/{orgId}/patientRecords/{patientRecordId}
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { db, auth, functions } from '../../../firebase';
import {
  pathwaysForConcern,
  pathwayTriageDocuments,
  resolveActingPathway,
  PATHWAY_LABELS,
} from '../../utils/triagePathways';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/useAuth';
import { canTriage, canOpenTriageScreens } from '../../../constants/userTypes';
import { formatDateShort } from '../../../utils/timestampUtils';
import { TriggerRmsDocumentGeneration } from '../../utils/TriggerRmsDocumentGeneration';
import { TriggerRmsDocumentRender } from '../../utils/TriggerRmsDocumentRender';
import { apiSignOffTriageLetter } from '../../utils/rmsApi';
import { hfpInstrumentForRecord } from '../../utils/referralStatus';
import { profileSharedWithClinicians } from '../../utils/profileSharing';
import SignOffTriageLetterDialog from '../../components/provider/SignOffTriageLetterDialog';
import QuestionnaireDownloadsPanel from '../../components/provider/QuestionnaireDownloadsPanel';
import ServiceUserUploadsPanel from '../../components/provider/ServiceUserUploadsPanel';
import useConfidentialDownload from '../../hooks/useConfidentialDownload';
import { toast } from 'react-toastify';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import ChildIdentityHeader from '../../components/provider/ChildIdentityHeader';
import {
  DocumentTextIcon,
  DownloadIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  LockClosedIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { WarningBar } from '../../../pages/providers/11_reviewDocuments/components/WarningBar';


// Section codes for the structured ADHD outcome letter, in order. Mirror of
// ADHD_DOC_STRUCTURE in RmsDocumentReview.js (evidence, other factors,
// conclusion, signposting) — keep in sync. Used to report real "N of 4
// sections approved" progress instead of a single monolithic section.
const STRUCTURED_OUTCOME_SECTION_CODES = ['evidence', 'otherFactors', 'conclusion', 'signposting'];

// TDI-693 (Tom King, R-025): the hover text on the disabled letter downloads,
// and the matching line in the not-sent notice.
const DOWNLOAD_AFTER_SIGN_OFF = "You can download the letter once it's signed off";

const RETRY_LOCK_MS = 60000;


// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function RmsDocumentsDashboard() {
  const { patientRecordId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  const [patientData, setPatientData] = useState(null);
  const [retrying, setRetrying] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Real-time Firestore listener
  // ─────────────────────────────────────────────────────────────────────────
  // Listens to the full patient record so we can show patient details in the
  // header and track triageDocuments.processingStatus for real-time updates.

  useEffect(() => {
    if (!orgId || !patientRecordId) return;

    const patientRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);

    const unsubscribe = onSnapshot(
      patientRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setPatientData(docSnap.data());
        }
      },
      (error) => {
        console.error('Error in RMS documents Firestore listener:', error);
      }
    );

    return () => unsubscribe();
  }, [orgId, patientRecordId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Derived state
  // ─────────────────────────────────────────────────────────────────────────

  // C8: the active pathway comes from the route (?pathway=, carried from the
  // triage flow), falling back to the viewing clinician's own specialism for
  // combined referrals. The old ADHD-first fallback sent an autism clinician
  // whose link had dropped the query param to the (often untriaged) ADHD
  // container, so their rendered letter looked deleted. The pathway names the
  // letter by condition, tells generation which pathway's reviews to use, and
  // selects that pathway's documents container.
  const intakeConcern = patientData?.intakeConcern;
  const effectivePathway = useMemo(() => {
    const routePathway = String(searchParams.get('pathway') || '').trim().toLowerCase();
    return (
      resolveActingPathway({ intakeConcern }, routePathway, claims?.specialism)
      || pathwaysForConcern(intakeConcern)[0]
      || null
    );
  }, [intakeConcern, searchParams, claims?.specialism]);

  // A combined referral holds a SEPARATE outcome letter per pathway (the ADHD
  // and autism letters coexist), so read the active pathway's documents
  // container; single-concern referrals use the top-level triageDocuments.
  const triageDocuments = useMemo(
    () => pathwayTriageDocuments(patientData, effectivePathway),
    [patientData, effectivePathway],
  );
  const processingStatus = triageDocuments?.processingStatus || 'idle';

  const letterType = triageDocuments?.letterType;

  const conditionLabel = effectivePathway ? PATHWAY_LABELS[effectivePathway] : '';
  const letterTitle = useMemo(() => {
    const base =
      letterType === 'accepted_letter'
        ? 'Referral Accepted Letter'
        : letterType === 'discharged_letter'
        ? 'Referral Not Accepted Letter'
        : 'Triage Outcome Letter';
    return conditionLabel ? `${conditionLabel} ${base}` : base;
  }, [letterType, conditionLabel]);

  // Document section status — drives the circle colour, background, and action buttons
  const documentStatus = useMemo(() => {
    if (processingStatus === 'processing') return 'processing';
    if (processingStatus === 'error') return 'error';

    if (!triageDocuments || !letterType) return 'locked';

    const section = triageDocuments.sections?.[letterType] || {};
    const hasOutputs = Object.keys(section.outputs || {}).length > 0;
    const isApproved = triageDocuments.reportApproved === true;

    if (isApproved) return 'approved';
    if (hasOutputs) return 'ready-for-review';
    if (processingStatus === 'complete') return 'ready-for-review';

    return 'locked';
  }, [processingStatus, triageDocuments, letterType]);

  // Deterministic ADHD letters are assembled from fixed templates (no AI-drafted
  // text), so they are auto-approved server-side and have no paragraphs to review.
  // We use this to suppress the "Review AI Outputs / Review Paragraphs" entry —
  // for these letters the clinician just views the generated document.
  const isDeterministicLetter = useMemo(() => {
    const section = triageDocuments?.sections?.[letterType] || {};
    return (section.inputs || {})._deterministic === true;
  }, [triageDocuments, letterType]);

  // documentPaths holds Firebase Storage paths (not public URLs).
  // Downloads go through rmsFetchTriageDocument which returns a signed URL.
  const documentPaths = triageDocuments?.documentPaths || {};
  const hasPdf = !!documentPaths.pdf;

  const patientName = useMemo(() => {
    const d = patientData || {};
    const first = d.subject?.firstName || d.subject_first_name || d.firstName || '';
    const last = d.subject?.lastName || d.subject_last_name || d.lastName || '';
    return [first, last].filter(Boolean).join(' ').trim() || 'Unknown Patient';
  }, [patientData]);

  const patientSex = patientData?.subject?.sex || null;
  const patientGender = patientData?.subject?.gender || null;
  const patientAge = patientData?.subject?.age || null;
  const referrerName = patientData?.referrer?.fullName || null;
  const referralDate = formatDateShort(patientData?.createdAt, '-');

  // Approval metadata for display
  const approvalInfo = useMemo(() => {
    if (!triageDocuments?.reportApproved) return null;
    return {
      approvedBy: triageDocuments.reportApprovedByName || 'Unknown',
    };
  }, [triageDocuments]);

  // Access control: only the clinician whose input fed the letter can review it
  const assignedReviewerId = triageDocuments?.assignedReviewerId || null;
  const assignedReviewerName = triageDocuments?.assignedReviewerName || null;
  const isCurrentUserAssigned = !assignedReviewerId || assignedReviewerId === auth.currentUser?.uid;

  // Overall progress. The structured outcome letters (ADHD and autism) have
  // four independently reviewed sections, so report real "N of 4 approved"
  // progress read from the same Firestore shape the review page approves into
  // (triageDocuments.sections[letterType].{outputs,approvedParagraphs}). The
  // legacy deterministic/LLM letters stay a single monolithic section (0% or
  // 100%).
  const { approvedCount, totalSections } = useMemo(() => {
    const section = triageDocuments?.sections?.[letterType] || {};
    if (letterType === 'adhd_outcome_letter' || letterType === 'autism_outcome_letter') {
      // Only count section codes that actually have generated content.
      const present = STRUCTURED_OUTCOME_SECTION_CODES.filter(
        (code) => section.outputs?.[code] !== undefined && section.outputs?.[code] !== '',
      );
      const total = present.length || STRUCTURED_OUTCOME_SECTION_CODES.length;
      const approved = present.filter(
        (code) => (section.approvedParagraphs || {})[code] !== undefined,
      ).length;
      return { approvedCount: approved, totalSections: total };
    }
    return { approvedCount: documentStatus === 'approved' ? 1 : 0, totalSections: 1 };
  }, [triageDocuments, letterType, documentStatus]);
  const progressPercent = totalSections > 0 ? (approvedCount / totalSections) * 100 : 0;

  // ─────────────────────────────────────────────────────────────────────────
  // Secure download via signed URL
  // ─────────────────────────────────────────────────────────────────────────

  const [downloading, setDownloading] = useState(false);

  // A user's first download for this patient shows the confidentiality
  // warning (TDI-484; once per user per patient, TDI-609); viewing inline
  // stays ungated — nothing is saved to the device.
  const { requestDownload, confirmDialog: downloadConfirmDialog } = useConfidentialDownload({
    orgId, patientRecordId, patientRecord: patientData,
  });

  /**
   * Request a short-lived signed URL from the backend and open it in a new
   * tab for download. Uses rmsFetchTriageDocument which mirrors the ADHD
   * assessment's fetchLatestDocumentForDownload pattern.
   */
  const handleDownload = useCallback(async (format, disposition = 'attachment') => {
    if (downloading) return;
    setDownloading(true);
    try {
      const fn = httpsCallable(functions, 'rmsFetchTriageDocument', { timeout: 30000 });
      const result = await fn({
        orgId,
        patientRecordId,
        fileFormat: format,
        disposition,
        ...(effectivePathway ? { pathway: effectivePathway } : {}),
      });
      const url = result.data?.downloadUrl;
      if (url) {
        window.open(url, '_blank');
      } else {
        console.error('rmsFetchTriageDocument returned no downloadUrl');
      }
    } catch (err) {
      console.error('Document download failed:', err);
    } finally {
      setDownloading(false);
    }
  }, [orgId, patientRecordId, downloading, effectivePathway]);

  // Open the rendered letter inline (view in a new tab) rather than download it.
  const handleView = useCallback(() => handleDownload('pdf', 'inline'), [handleDownload]);

  // The letter is opened on demand via the View action only. We deliberately do
  // NOT auto-open it on arrival from the review flow: approving the paragraphs
  // would pop up the letter, and (because a fresh render lags approval) it could
  // show the previous, pre-edit render. The clinician opens it when ready.

  // ─────────────────────────────────────────────────────────────────────────
  // Send the letter to service-user recipients (TD-145)
  // ─────────────────────────────────────────────────────────────────────────
  // Sending delivers the rendered letter to every linked service-user
  // recipient (parent/carer AND school rep — the referrer always receives the
  // outcome letter); it becomes viewable in their shared portal and they are
  // emailed. The dialog confirms the fixed recipient list.

  const sharedRoles = useMemo(
    () => (Array.isArray(triageDocuments?.sharedRoles) ? triageDocuments.sharedRoles : []),
    [triageDocuments]
  );

  // ── Clinician sign-off (TD-249) ──────────────────────────────────────────
  // The rendered letter must be signed off (mandatory double confirmation)
  // before it can be sent. Sign-off makes it final and irreversible: the
  // backend blocks regeneration, re-rendering and sending-without-sign-off,
  // and the review surface goes read-only.
  const signOff = triageDocuments?.signOff || null;
  const isSignedOff = Boolean(signOff);
  // TDI-693: the letter downloads only once signed off (per pathway on a
  // combined referral, since triageDocuments is the pathway's own letter).
  // The server refuses an earlier download too.
  const canDownloadLetter = hasPdf && isSignedOff;
  const [showSignOffDialog, setShowSignOffDialog] = useState(false);
  const [signingOff, setSigningOff] = useState(false);

  // Arriving from a completed AI-output review (?signoff=1): sign-off is the
  // end of that review, not a separate errand the clinician has to remember to
  // come back for (Tom King, 29 Sept). It has to wait for the render, because
  // the clinician is attesting to the final letter, so this opens as soon as
  // the PDF lands. Prompted once per arrival - dismissing it leaves the
  // ordinary Sign off letter button, so nobody is trapped in the dialog.
  const signOffPrompted = useRef(false);
  useEffect(() => {
    if (signOffPrompted.current) return;
    if (searchParams.get('signoff') !== '1') return;
    if (!hasPdf || isSignedOff || !canTriage(claims)) return;
    signOffPrompted.current = true;
    setShowSignOffDialog(true);
  }, [searchParams, hasPdf, isSignedOff, claims]);

  const handleSignOff = useCallback(async () => {
    if (signingOff) return;
    setSigningOff(true);
    try {
      await apiSignOffTriageLetter({
        orgId,
        patientRecordId,
        pathway: effectivePathway || undefined,
      });
      toast.success('Letter signed off. It is now final and can be sent.');
      setShowSignOffDialog(false);
    } catch (err) {
      console.error('Failed to sign off triage letter:', err);
      toast.error(err?.message || 'Could not sign off the letter. Please try again.');
    } finally {
      setSigningOff(false);
    }
  }, [orgId, patientRecordId, effectivePathway, signingOff]);

  // Sending the letter is not done from here (TDI-387, Tom King 23 and 24
  // October 2026): the admin roles send it from the Admin Handover screen,
  // which is what starts their EMIS work. This page signs off and shows
  // status only.

  const sentLabel = useMemo(() => {
    if (!sharedRoles.length) return '';
    return sharedRoles
      .map((r) => (r === 'guardian' ? 'parent/carer' : 'school representative'))
      .join(' and ');
  }, [sharedRoles]);

  // ─────────────────────────────────────────────────────────────────────────
  // Retry — re-triggers generation or rendering depending on where the
  // pipeline failed. If AI outputs already exist, only re-render; otherwise
  // kick off the full generation flow from scratch.
  // ─────────────────────────────────────────────────────────────────────────

  // Determine which pipeline stage we're in (generating paragraphs vs
  // rendering the final document) so badges and messages are contextual.
  const hasApprovedOutputs = useMemo(() => {
    const section = triageDocuments?.sections?.[letterType] || {};
    const hasOutputs = Object.keys(section.outputs || {}).length > 0;
    return hasOutputs && triageDocuments?.reportApproved === true;
  }, [triageDocuments, letterType]);

  const isRendering = processingStatus === 'processing' && hasApprovedOutputs;
  const isRenderError = processingStatus === 'error' && hasApprovedOutputs;

  const handleRetry = useCallback(() => {
    if (retrying || !orgId || !patientRecordId) return;
    setRetrying(true);

    const pathwayArg = effectivePathway ? { pathway: effectivePathway } : {};
    if (isRenderError) {
      // The render is fire-and-forget, and only the server moves the status
      // off 'error', a moment after it starts. Retry stays disabled until it
      // does (see the effect below); releasing it here let every click in that
      // gap start another render of the same letter.
      TriggerRmsDocumentRender({ orgId, patientRecordId, ...pathwayArg })
        .then((result) => {
          if (!result?.success) setRetrying(false);
        });
    } else {
      TriggerRmsDocumentGeneration({ orgId, patientRecordId, ...pathwayArg });
      setRetrying(false);
    }
  }, [retrying, orgId, patientRecordId, isRenderError, effectivePathway]);

  // Release Retry once the server has picked the render up (status leaves
  // 'error'), or after RETRY_LOCK_MS if it never does - a call rejected before
  // the server records a status must not leave the button stuck.
  useEffect(() => {
    if (!retrying) return undefined;
    if (processingStatus !== 'error') {
      setRetrying(false);
      return undefined;
    }
    const timer = setTimeout(() => setRetrying(false), RETRY_LOCK_MS);
    return () => clearTimeout(timer);
  }, [retrying, processingStatus]);

  // ─────────────────────────────────────────────────────────────────────────
  // Breadcrumbs — reuses RmsTopNavBar's breadcrumb prop
  // ─────────────────────────────────────────────────────────────────────────

  const breadcrumbs = [
    { label: 'Home', path: '/rms/provider/dashboard' },
    // Carry the active pathway so a combined referral's overview keeps showing
    // THIS pathway's triage state after the breadcrumb hop.
    { label: 'Patient Overview', path: `/rms/provider/patient/${patientRecordId}${effectivePathway ? `?pathway=${effectivePathway}` : ''}` },
    { label: 'Documents', path: null },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // UI Helpers — ADHD dashboard-style circle and row colouring
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Returns Tailwind classes for the numbered circle based on section status.
   * Matches the ADHD dashboard's getCircleClass pattern with RMS brand colours.
   */
  const getCircleClass = useCallback(() => {
    switch (documentStatus) {
      case 'approved':
        return 'border-green-500 bg-green-50 text-green-700';
      case 'ready-for-review':
        return 'border-primary bg-primary/10 text-primary';
      case 'processing':
        return 'border-amber-400 bg-amber-50 text-amber-700';
      case 'error':
        return 'border-red-400 bg-red-50 text-red-700';
      default:
        return 'border-gray-300 bg-gray-50 text-gray-400';
    }
  }, [documentStatus]);

  /**
   * Returns Tailwind classes for the section row background.
   * Matches the ADHD dashboard's getBackgroundClass pattern.
   */
  const getBackgroundClass = useCallback(() => {
    switch (documentStatus) {
      case 'approved':
        return 'bg-green-50 border border-green-200';
      case 'ready-for-review':
        return 'bg-primary/5 border border-primary/20';
      case 'processing':
        return 'bg-amber-50 border border-amber-200';
      case 'error':
        return 'bg-red-50 border border-red-200';
      default:
        return 'bg-gray-50 border border-gray-200';
    }
  }, [documentStatus]);

  /**
   * Renders the status icon/badge for the document section.
   * Mirrors the ADHD dashboard's getStatusIcon function.
   */
  const renderStatusIcon = useCallback(() => {
    switch (documentStatus) {
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
            <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            {isRendering ? 'Finalising document...' : 'Generating...'}
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
            <ExclamationCircleIcon className="w-3.5 h-3.5" />
            Error
          </span>
        );
      case 'ready-for-review':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
            <DocumentTextIcon className="w-3.5 h-3.5" />
            Ready for Review
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3.5 h-3.5" />
            Approved
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
            <LockClosedIcon className="w-3.5 h-3.5" />
            Awaiting generation
          </span>
        );
    }
  }, [documentStatus, isRendering]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      {/* RmsTopNavBar with Documents breadcrumb */}
      <RmsTopNavBar breadcrumbs={breadcrumbs} />
      {/* The child's identity, the same on every triage screen (TDI-392). */}
      <ChildIdentityHeader patient={patientData} patientRecordId={patientRecordId} loading={!patientData} />

      <div className="flex-1 px-6 sm:px-8 lg:px-12 py-8">

        {/* ════════════════════════════════════════════════════════════════════
            Patient Header Card
            Mirrors the header panel in RmsPatientOverview.js, with a single
            "Go to Patient Overview" action button instead of the review/docs buttons.
            ════════════════════════════════════════════════════════════════════ */}
        {patientData && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
            <div className="flex items-center justify-between">
              {/* Left: Patient info */}
              <div className="flex items-start gap-8">
                {/* Name and demographics */}
                <div>
                  {/* The name, date of birth, NHS number and reference are in
                      the shared header above (TDI-392); this card keeps what
                      that header does not carry. */}
                  <h1 className="text-xl font-bold text-gray-900 mb-1">Documents</h1>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    {patientSex && (
                      <span>
                        <span className="text-gray-400">Sex:</span>{' '}
                        <span className="font-medium">{patientSex}</span>
                      </span>
                    )}
                    {patientGender && (
                      <span>
                        <span className="text-gray-400">Gender:</span>{' '}
                        <span className="font-medium">{patientGender}</span>
                      </span>
                    )}
                    {patientAge && (
                      <span>
                        <span className="text-gray-400">Age:</span>{' '}
                        <span className="font-medium">{patientAge}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div className="h-12 w-px bg-gray-200" />

                {/* Metadata columns */}
                <div className="flex gap-8">
                  {referrerName && (
                    <div className="text-center">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Referred by</p>
                      <p className="text-sm font-medium text-gray-800">{referrerName}</p>
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Referred on</p>
                    <p className="text-sm font-medium text-gray-800">{referralDate}</p>
                  </div>
                </div>
              </div>

              {/* Right: Action button */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/rms/provider/patient/${patientRecordId}${effectivePathway ? `?pathway=${effectivePathway}` : ''}`)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Go to Patient Overview
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Page order (TDI-602 FR2, Tom King, 30 October 2026): the outcome
            letter, the questionnaires, the Service User Uploads, then the
            Holistic Functional Profile at the bottom when it applies. */}
        {/* ════════════════════════════════════════════════════════════════════
            Main Triage Report Panel
            Mirrors the ADHD "Clinical Assessment Report" panel structure:
            title + progress bar + download button in the header,
            connected numbered section rows in the body.
            ════════════════════════════════════════════════════════════════════ */}
        <div className="max-w-6xl p-8 mx-auto mb-4 text-left bg-white rounded-xl shadow-sm border transition-all duration-200">

          {/* AI-content disclaimer — the outcome letter is AI-generated, so the
              clinician is reminded of their responsibility to verify it, mirroring
              the assessment platform's review pages. */}
          <WarningBar show message="This outcome letter contains AI-generated content. Please review it carefully as AI can make mistakes — you are responsible for verifying all content before it is sent." />

          {/* Header with Progress */}
          <div className="flex items-center justify-between ml-4 mb-2">
            {/* Left: Title */}
            <h1 className="text-2xl font-bold text-gray-900">Triage Outcome Letter</h1>

            {/* Right: Progress and Download */}
            <div className="flex items-center gap-6">
              {/* Progress Section */}
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <h2 className="text-sm font-medium text-gray-700">Overall Progress</h2>
                  <p className="text-xs text-gray-600">
                    {approvedCount} of {totalSections} section{totalSections !== 1 ? 's' : ''} approved
                  </p>
                </div>
                <div className="w-48 h-2 bg-gray-200 rounded-full">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      progressPercent === 100 ? 'bg-green-500' : 'bg-primary'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {/* View Button — opens the rendered PDF inline in a new tab,
                  mirroring the assessment platform's "open the letter" flow. */}
              {hasPdf && (
                <button
                  disabled={downloading}
                  onClick={handleView}
                  className={`px-6 py-2.5 text-sm font-bold rounded-md
                    focus:outline-none focus:ring-2 focus:ring-primary
                    focus:ring-offset-2 flex items-center justify-center
                    ${!downloading
                      ? 'bg-white text-primary border border-primary cursor-pointer hover:bg-primary/5'
                      : 'bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed'
                    }`}
                >
                  View Letter
                </button>
              )}

              {/* Download Button — fetches a signed URL via Cloud Function.
                  PDF only (TDI-484): the DOCX fallback and button are gone,
                  and the download first shows the confidentiality warning.
                  Only a signed-off letter downloads (TDI-693): before that the
                  file is not final, so it must not leave the platform looking
                  final. Viewing stays, because sign-off needs it. */}
              <button
                disabled={!canDownloadLetter || downloading}
                title={hasPdf && !isSignedOff ? DOWNLOAD_AFTER_SIGN_OFF : undefined}
                onClick={() => requestDownload(() => handleDownload('pdf'))}
                className={`px-6 py-2.5 text-sm font-bold rounded-md
                  focus:outline-none focus:ring-2 focus:ring-primary
                  focus:ring-offset-2 flex items-center justify-center
                  ${canDownloadLetter && !downloading
                    ? 'bg-primary text-white cursor-pointer hover:bg-primary/90'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {processingStatus === 'processing' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                    {isRendering ? 'Finalising...' : 'Generating...'}
                  </>
                ) : downloading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <DownloadIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                    Download Letter
                  </>
                )}
              </button>

              {/* No send action here (TDI-387): the letter is sent, once, by
                  the admin roles from the Admin Handover screen. */}

              {/* Sign-off Button (TD-249) — the mandatory double-confirmation
                  step between rendering and sending. Clinician-only; once
                  signed off the letter is final and irreversible. One control
                  per page: while the letter is unsent the sign-off action lives
                  in the not-sent notice below, so this header action only shows
                  for an already-sent letter that predates sign-off (sentLabel
                  set, no signOff) - letting a clinician sign it off to re-send. */}
              {hasPdf && canTriage(claims) && !isSignedOff && sentLabel && (
                <button
                  type="button"
                  disabled={signingOff}
                  onClick={() => setShowSignOffDialog(true)}
                  className="px-6 py-2.5 text-sm font-bold rounded-md bg-primary text-white cursor-pointer hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center justify-center disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  Sign off letter
                </button>
              )}
            </div>
          </div>

          {/* Signed-off status (TD-249) — the letter is final and locked. */}
          {isSignedOff && (
            <p className="mt-1 text-sm text-green-700 flex items-center gap-1.5">
              <CheckCircleIcon className="w-4 h-4" aria-hidden="true" />
              Signed off by {signOff?.signedOffBy?.clinicianName || 'a clinician'}
              {signOff?.signedOffAt ? ` on ${formatDateShort(signOff.signedOffAt, '')}` : ''}.
              The letter is final and can no longer be edited.
            </p>
          )}

          {/* Sent status — who the letter has been shared with (TD-145). */}
          {sentLabel && (
            <p className="mt-1 text-sm text-green-700 flex items-center gap-1.5">
              <CheckCircleIcon className="w-4 h-4" aria-hidden="true" />
              Sent to the {sentLabel}.
            </p>
          )}

          {/* Not-sent notice — the rendered letter stays private to clinicians
              until it is explicitly sent (TD-145), while the referral status
              updates for the family as soon as the decision is recorded. Make
              that gap unmissable so completing the letter is never mistaken
              for having notified the parent/carer and school. */}
          {hasPdf && !sentLabel && (
            <div className="mt-4 ml-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
              <ExclamationCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
              <div className="text-left">
                <p className="text-sm font-semibold text-amber-800">
                  This letter has not been sent to the parent/carer or school representative yet.
                </p>
                <p className="mt-0.5 text-sm text-amber-800">
                  They can already see the updated referral status, but they will not receive an
                  email or see the letter in their portal until it is sent.
                  {!isSignedOff && ' The letter must be signed off by a clinician before it can be sent.'}
                  {!isSignedOff && ` ${DOWNLOAD_AFTER_SIGN_OFF}.`}
                  {!canTriage(claims) && ' Only a clinician can sign it off.'}
                  {isSignedOff && ' The support team sends it from the Admin Handover screen.'}
                </p>
                {/* Signed off → the admin roles send it from the Admin
                    Handover screen (TDI-387); nothing to do here. Not signed
                    off → only a clinician can sign it off (TD-249). */}
                {!isSignedOff && canTriage(claims) && (
                  <button
                    type="button"
                    disabled={signingOff}
                    onClick={() => setShowSignOffDialog(true)}
                    className="mt-2 px-4 py-1.5 text-sm font-bold rounded-md bg-primary text-white cursor-pointer hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    Sign off letter
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              Connected Document Sections
              Vertical connector line + numbered circle for each section.
              The RMS version has a single section, but the structure supports
              adding more sections in the future (e.g. cover page, appendices).
              ══════════════════════════════════════════════════════════════════ */}
          <div className="relative mt-6">
            {/* Vertical connecting line */}
            <div className="absolute left-[40px] top-6 bottom-6 w-0.5 bg-gray-200" />

            {/* Section Row: Triage Outcome Letter */}
            <div className="relative">
              <div
                className={`
                  w-full flex items-center gap-6 my-1 py-3 px-4 rounded-lg transition-all duration-200
                  ${getBackgroundClass()}
                `}
              >
                {/* Numbered circle */}
                <div className={`
                  relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 
                  flex items-center justify-center font-semibold text-lg
                  ${getCircleClass()}
                `}>
                  1
                </div>

                {/* Section Info */}
                <div className="flex-1 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{letterTitle}</h3>
                      {renderStatusIcon()}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {documentStatus === 'approved' && approvalInfo ? (
                        <p className="text-sm italic text-gray-700">
                          AI outputs approved by{' '}
                          <span className="inline-flex border items-center gap-2 px-3 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                            {approvalInfo.approvedBy}
                          </span>
                        </p>
                      ) : documentStatus === 'ready-for-review' ? (
                        <p className="text-sm italic text-gray-700">
                          AI outputs ready for review
                        </p>
                      ) : documentStatus === 'processing' ? (
                        <p className="text-sm italic text-amber-700">
                          {isRendering
                            ? 'Rendering document - this may take up to a minute...'
                            : 'Generating paragraphs - this may take up to a minute...'}
                        </p>
                      ) : documentStatus === 'error' ? (
                        <p className="text-sm italic text-red-700">
                          {isRenderError
                            ? 'An error occurred while rendering the document. Please try again.'
                            : 'An error occurred during paragraph generation. Please try again.'}
                        </p>
                      ) : (
                        <p className="text-sm italic text-gray-500">
                          Awaiting triage decision to generate letter
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {/* Retry button — shown when generation or rendering failed.
                        Retrying re-triggers generation/render (a clinician
                        action), so it is hidden from RMS Support / non-clinical
                        admins, who the backend would reject anyway. */}
                    {documentStatus === 'error' && canTriage(claims) && (
                      <button
                        onClick={handleRetry}
                        disabled={retrying}
                        className="flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {retrying ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin mr-1.5" />
                            Retrying...
                          </>
                        ) : (
                          <>
                            <RefreshIcon className="w-4 h-4 mr-1.5" />
                            Retry
                          </>
                        )}
                      </button>
                    )}

                    {/* Locked section explainer */}
                    {documentStatus === 'locked' && (
                      <div className="text-xs text-gray-500 italic">
                        Completes after triage decision
                      </div>
                    )}

                    {/* Review AI Outputs button — only the assigned reviewer can navigate.
                        Hidden for deterministic ADHD letters (nothing to review) and
                        for RMS Support, who cannot open the review screen (the route
                        would bounce them) — they still get View/Download. */}
                    {!isDeterministicLetter && (documentStatus === 'ready-for-review' || documentStatus === 'approved') && isCurrentUserAssigned && canOpenTriageScreens(claims) && (
                      <button
                        onClick={() =>
                          navigate(
                            `/rms/provider/patient/${patientRecordId}/review-document${effectivePathway ? `?pathway=${effectivePathway}` : ''}`,
                          )
                        }
                        className={`
                          flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                          ${documentStatus === 'ready-for-review'
                            ? 'bg-primary text-white hover:bg-primary/90'
                            : 'text-gray-900 border border-gray-300 hover:bg-green-200'
                          }
                        `}
                      >
                        {documentStatus === 'ready-for-review' ? 'Review AI Outputs' : 'Review Paragraphs'}
                      </button> 
                    )}

                    {/* Assigned-to indicator — shown to other clinicians who aren't the reviewer */}
                    {(documentStatus === 'ready-for-review' || documentStatus === 'approved') && !isCurrentUserAssigned && assignedReviewerName && (
                      <div className="inline-flex border items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        Assigned to {assignedReviewerName}
                      </div>
                    )}

                    {/* Download via signed URL. PDF only (TDI-484): the DOCX
                        button is gone, and the download first shows the
                        confidentiality warning. */}
                    {documentStatus === 'approved' && hasPdf && (
                      <button
                        onClick={() => requestDownload(() => handleDownload('pdf'))}
                        disabled={!canDownloadLetter || downloading}
                        title={!isSignedOff ? DOWNLOAD_AFTER_SIGN_OFF : undefined}
                        className="text-gray-900 border border-gray-300 hover:bg-gray-100 disabled:opacity-50 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                      >
                        <DownloadIcon className="w-4 h-4 mr-1.5" />
                        PDF
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Questionnaire answers (per respondent), each SNAP-IV and each
            completed triage decision as PDFs for EMIS entry. Never the HFP. */}
        <QuestionnaireDownloadsPanel
          orgId={orgId}
          patientRecordId={patientRecordId}
          patientRecord={patientData}
        />

        {/* The documents a school or a family attached (TDI-602), after the
            questionnaires and laid out like them. Read-only here: they
            belong to whoever uploaded them. */}
        <ServiceUserUploadsPanel
          orgId={orgId}
          patientRecordId={patientRecordId}
          patientRecord={patientData}
          variant="panel"
        />

        {/* ════════════════════════════════════════════════════════════════════
            Holistic Functional Profile document entry
            ════════════════════════════════════════════════════════════════════ */}
        {/* The entry keys off the record's own instrument's scoring
            (hfpInstrumentForRecord): v3 for Harbour pupils, v2 for legacy
            referrals, or whatever the record was created on (TDI-414). */}
        {/* TDI-483: only when the family has shared the profile with the
            clinical team; otherwise there is no document to list. */}
        {patientData?.domains?.[`hfp${hfpInstrumentForRecord(patientData).toUpperCase()}`]?.scoring
          && profileSharedWithClinicians(patientData) && (
          <div className="max-w-6xl mx-auto mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Holistic Functional Profile</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Strengths, support needs and reasonable adjustments at home and at school.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/rms/provider/patient/${patientRecordId}/hfp`)}
                  className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  View
                </button>
                <button
                  type="button"
                  disabled
                  title="PDF download will be available soon"
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-400 text-sm font-semibold cursor-not-allowed"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* The confidentiality confirm every download passes through (TDI-484). */}
      {downloadConfirmDialog}

      {/* Mandatory clinician double-confirmation sign-off (TD-249). */}
      <SignOffTriageLetterDialog
        open={showSignOffDialog}
        onOpenChange={setShowSignOffDialog}
        patientName={patientName}
        signing={signingOff}
        onConfirm={handleSignOff}
      />
    </div>
  );
}
